import { ApolloLink, Observable } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { isOutdatedClientError } from "./graphqlErrorCodes";

let outdated = false;
const listeners = new Set<() => void>();

/**
 * Whether the loaded bundle is known to be older than the version deployed on the server
 */
export const isClientOutdated = () => outdated;

/**
 * Remember that the server rejected a persisted query of this bundle and notify subscribers.
 * Only the first call has an effect, the state is never reset - the page has to be reloaded.
 */
export const markClientOutdated = () => {
  if (outdated) {
    return;
  }
  outdated = true;
  listeners.forEach((listener) => listener());
};

/**
 * Subscribe to the outdated state, returns an unsubscribe function
 */
export const subscribeClientOutdated = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Observable that never emits, used to park an operation.
 *
 * Nothing sensible can be done with a request of an outdated bundle: the server refuses it and retrying it with
 * the full query text is rejected as well (the API only accepts persisted queries). Parking the operation keeps
 * the consuming component in its loading state, so the user sees the reload prompt instead of a crashed view.
 */
const interruptedOperation = <T = FetchResult>() => new Observable<T>(() => undefined);

/**
 * Link stopping every operation once the bundle is known to be outdated.
 *
 * Without it each of the remaining components subscribed to the same query would fire its own doomed request
 * (a failed query is never cached, and Apollo only deduplicates requests overlapping in flight), which is how a
 * single stale tab produces dozens of failing requests within a few seconds.
 */
export const requestClosureGuardLink = new ApolloLink((operation, forward) =>
  isClientOutdated() ? interruptedOperation() : forward(operation)
);

/**
 * Link turning a response that this bundle cannot recover from into a reload prompt.
 *
 * Placed below the reporting error link, so the parked response never reaches it - a stale tab is not an
 * application error worth reporting from the client, and the API reports the rejected operation on its own.
 */
export const invalidPersistedQueriesErrorLink = onError(({ graphQLErrors }) => {
  if (!isOutdatedClientError(graphQLErrors)) {
    return undefined;
  }
  markClientOutdated();
  return interruptedOperation();
});
